/**
 * App-wide (session-scoped) sink for Centrifugo transport diagnostics.
 *
 * The GG reconnect/disconnect chart on the dev dashboard is fed by
 * `RtcDiagnosticEvent` rows with `event_type` starting `centrifugo_`. Those
 * events are emitted on the diagnostics bus by `chat/api/realtime.ts` on every
 * (re)connect, disconnect, error and resubscribe — but the bus DROPS an event
 * when nobody is subscribed (`diagnosticsBus.ts`). Until now the only
 * subscribers were per-call `rtcLogger`s, created solely during a screenshare or
 * meeting. So outside a live call these transport events were never persisted
 * and the chart stayed empty in normal usage (i.e. on deploy).
 *
 * This module keeps ONE always-on logger alive for as long as the realtime
 * transport exists, so reconnect storms in the plain GG chat surface on the
 * chart. It reuses `createRtcLogger` (buffering, batched POST, pagehide
 * keepalive flush) with the dedicated `transport` session kind, so these
 * app-wide events never get counted twice against a concurrent meeting session.
 */

import { createRtcLogger, type RtcLogger } from "./rtcLogger";

let logger: RtcLogger | null = null;
let sessionId: string | null = null;

function newSessionId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // Extremely old browser without crypto.randomUUID — fall back to a v4-ish
    // string. The backend only needs a parseable UUID; collisions don't matter
    // for minute-bucket counts.
    return "00000000-0000-4000-8000-" + Date.now().toString(16).padStart(12, "0");
  }
}

/**
 * What the transport sink keeps. Every open tab — hidden ones too, all day — renews
 * each channel token every ~10 min, resubscribes every channel on each reconnect and
 * notes every tab switch; persisting that routine made /realtime/diagnostics/ the
 * most frequent request of the whole API (~24k POSTs a day, 2026-10-20) while the
 * dev chart reads only the transport incidents. Routine successes stay live-only.
 */
export function isTransportIncident(type: string, payload?: Record<string, unknown>): boolean {
  if (type === "page") return false;
  if (type === "token_refresh") return payload?.outcome !== "ok";
  // A subscribe that lost its history (recovery failed) means missed messages.
  if (type === "centrifugo_subscribed") return payload?.wasRecovering === true && payload?.recovered !== true;
  return true;
}

/** Incidents are counted per minute on the dev chart — no hurry to ship them. */
const TRANSPORT_FLUSH_INTERVAL_MS = 60_000;

/** Start the app-wide transport diagnostics sink. Idempotent. */
export function startTransportDiagnostics(): void {
  if (logger) return;
  sessionId ??= newSessionId();
  logger = createRtcLogger({
    kind: "transport",
    sessionId,
    persist: isTransportIncident,
    flushIntervalMs: TRANSPORT_FLUSH_INTERVAL_MS,
  });
}

/** Stop and flush the sink (on logout / realtime teardown). Idempotent. */
export function stopTransportDiagnostics(): void {
  if (!logger) return;
  const l = logger;
  logger = null;
  try {
    l.dispose();
  } catch {
    // Best-effort — diagnostics teardown must never throw into logout.
  }
}
